import { Check, Download, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
	useDdsAttendanceDrilldown,
	useExportDdsAttendance,
} from '@/api/queries/kpi-metrics-queries'
import { SearchInput } from '@/components/commons'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type {
	DDSAttendanceDdsEntry,
	DDSAttendancePersonEntry,
	KpiMetricsParams,
} from '@/types'
import { downloadBlob } from '@/utils/download-blob'

interface DdsAttendanceDialogProps {
	cr: { id: string; name: string } | null
	params?: KpiMetricsParams
	onOpenChange: (open: boolean) => void
}

// API returns dates at UTC midnight — format in UTC to avoid shifting a day back
function formatDdsDate(date: string) {
	return new Date(date).toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		timeZone: 'UTC',
	})
}

function pct(value: number, total: number) {
	return total > 0 ? (value / total) * 100 : 0
}

export function DdsAttendanceDialog({
	cr,
	params,
	onOpenChange,
}: DdsAttendanceDialogProps) {
	const { t } = useTranslation()
	const [search, setSearch] = useState('')
	const { data, isLoading } = useDdsAttendanceDrilldown(cr?.id, params)
	const { mutate: exportAttendance, isPending: isExporting } =
		useExportDdsAttendance()

	const handleExport = () => {
		if (!cr) return
		exportAttendance(
			{ crId: cr.id, params },
			{
				onSuccess: (blob) => downloadBlob(blob, `presenca-dds-${cr.name}.xlsx`),
				onError: () => {
					toast.error(t('kpiMetrics.dds.attendance.exportError'))
				},
			},
		)
	}

	const ddsList = useMemo(
		() =>
			[...(data?.ddsList ?? [])].sort((a, b) => a.date.localeCompare(b.date)),
		[data],
	)

	const people = useMemo(() => {
		const term = search.trim().toLowerCase()
		const list = data?.people ?? []
		if (!term) return list
		return list.filter(
			(p) => p.name.toLowerCase().includes(term) || p.badge.includes(term),
		)
	}, [data, search])

	const totalPeople = data?.people.length ?? 0
	const totalPresences = ddsList.reduce((sum, d) => sum + d.presentCount, 0)
	const avgPct = pct(totalPresences, totalPeople * ddsList.length)

	return (
		<Dialog
			open={!!cr}
			onOpenChange={(open) => {
				if (!open) setSearch('')
				onOpenChange(open)
			}}
		>
			<DialogContent className="flex max-h-[90vh] flex-col sm:max-w-6xl">
				<DialogHeader className="flex-row items-start justify-between gap-3 space-y-0 pr-8">
					<div>
						<DialogTitle>{cr?.name}</DialogTitle>
						<DialogDescription>
							{t('kpiMetrics.dds.attendance.subtitle')}
						</DialogDescription>
					</div>
					{!isLoading && data && ddsList.length > 0 && totalPeople > 0 && (
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="shrink-0"
							disabled={isExporting}
							onClick={handleExport}
						>
							<Download className="size-4" />
							{isExporting
								? t('kpiMetrics.dds.attendance.exporting')
								: t('kpiMetrics.dds.attendance.export')}
						</Button>
					)}
				</DialogHeader>

				{isLoading || !data ? (
					<div className="flex flex-col gap-2">
						{['a', 'b', 'c', 'd', 'e'].map((key) => (
							<Skeleton key={key} className="h-8 w-full" />
						))}
					</div>
				) : ddsList.length === 0 || totalPeople === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						{t('kpiMetrics.charts.noData')}
					</p>
				) : (
					<>
						<div className="flex flex-wrap items-center justify-between gap-3">
							<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
								<SummaryItem
									label={t('kpiMetrics.dds.attendance.ddsCount')}
									value={String(ddsList.length)}
								/>
								<SummaryItem
									label={t('kpiMetrics.dds.attendance.peopleCount')}
									value={String(totalPeople)}
								/>
								<SummaryItem
									label={t('kpiMetrics.dds.attendance.avgAttendance')}
									value={`${avgPct.toFixed(1)}%`}
								/>
							</div>
							<SearchInput
								value={search}
								onChange={setSearch}
								placeholder={t('kpiMetrics.dds.attendance.searchPlaceholder')}
								className="w-full sm:w-64"
							/>
						</div>

						<AttendanceTable ddsList={ddsList} people={people} />
					</>
				)}
			</DialogContent>
		</Dialog>
	)
}

function SummaryItem({ label, value }: { label: string; value: string }) {
	return (
		<span>
			{label}:{' '}
			<span className="font-semibold text-kaefer-dark-grey tabular-nums">
				{value}
			</span>
		</span>
	)
}

function AttendanceTable({
	ddsList,
	people,
}: {
	ddsList: DDSAttendanceDdsEntry[]
	people: DDSAttendancePersonEntry[]
}) {
	const { t } = useTranslation()
	const stickyCol = 'sticky left-0 z-10 bg-white'

	return (
		<div className="min-h-0 flex-1 overflow-auto rounded-md border border-kaefer-light-silver [&_[data-slot=table-container]]:overflow-visible">
			<Table>
				<TableHeader className="sticky top-0 z-20 bg-white">
					<TableRow className="hover:bg-transparent">
						<TableHead className={cn(stickyCol, 'min-w-56')}>
							{t('kpiMetrics.dds.attendance.person')}
						</TableHead>
						<TableHead className="text-center">
							{t('kpiMetrics.dds.attendance.presence')}
						</TableHead>
						{ddsList.map((dds) => (
							<TableHead key={dds.ddsId} className="text-center">
								<DdsColumnHeader dds={dds} />
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{people.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={ddsList.length + 2}
								className="py-8 text-center text-muted-foreground"
							>
								{t('kpiMetrics.dds.attendance.noPeople')}
							</TableCell>
						</TableRow>
					) : (
						people.map((person) => (
							<TableRow key={person.personId} className="group">
								<TableCell className={cn(stickyCol, 'group-hover:bg-muted')}>
									<div className="flex flex-col">
										<span className="font-medium capitalize text-kaefer-dark-grey">
											{person.name}
										</span>
										<span className="text-[11px] text-muted-foreground">
											{person.badge} · {person.jobRole}
										</span>
									</div>
								</TableCell>
								<TableCell className="text-left">
									<PersonRate
										present={person.presentCount}
										total={ddsList.length}
									/>
								</TableCell>
								{ddsList.map((dds) => (
									<TableCell key={dds.ddsId} className="text-center">
										<PresenceMark present={!!person.attendance[dds.ddsId]} />
									</TableCell>
								))}
							</TableRow>
						))
					)}
				</TableBody>
				<TableFooter className="bg-muted">
					<TableRow className="hover:bg-transparent">
						<TableCell className={cn(stickyCol, 'bg-muted font-semibold')}>
							{t('kpiMetrics.dds.attendance.presentPerDds')}
						</TableCell>
						<TableCell className="text-center font-semibold tabular-nums"></TableCell>
						{ddsList.map((dds) => (
							<TableCell
								key={dds.ddsId}
								className="text-center font-semibold tabular-nums"
							>
								{dds.presentCount}
							</TableCell>
						))}
						<TableCell />
					</TableRow>
				</TableFooter>
			</Table>
		</div>
	)
}

function DdsColumnHeader({ dds }: { dds: DDSAttendanceDdsEntry }) {
	const { t } = useTranslation()
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className="mx-auto flex max-w-28 cursor-default flex-col items-center py-1">
					<span className="font-semibold tabular-nums text-kaefer-dark-grey">
						{formatDdsDate(dds.date)}
					</span>
					<span className="w-full truncate text-[11px] font-normal text-muted-foreground">
						{dds.subject.trim()}
					</span>
				</div>
			</TooltipTrigger>
			<TooltipContent side="top">
				<div className="flex flex-col gap-0.5">
					<span className="font-semibold">{dds.subject.trim()}</span>
					<span className="capitalize">
						{t('kpiMetrics.dds.attendance.createdBy')}: {dds.createdByName}
					</span>
				</div>
			</TooltipContent>
		</Tooltip>
	)
}

function PresenceMark({ present }: { present: boolean }) {
	const { t } = useTranslation()
	const label = present
		? t('kpiMetrics.dds.attendance.present')
		: t('kpiMetrics.dds.attendance.absent')

	return (
		<span
			role="img"
			aria-label={label}
			title={label}
			className={cn(
				'inline-flex size-6 items-center justify-center rounded-full',
				present
					? 'bg-kaefer-forest-green/15 text-kaefer-forest-green'
					: 'bg-destructive/10 text-destructive',
			)}
		>
			{present ? <Check className="size-3.5" /> : <X className="size-3.5" />}
		</span>
	)
}

function PersonRate({ present, total }: { present: number; total: number }) {
	const value = pct(present, total)
	return (
		<div className="flex items-center justify-end gap-2">
			<div className="h-2 w-14 overflow-hidden rounded-full bg-kaefer-light-silver/60">
				<div
					className="h-full rounded-full bg-kaefer-sapphire-blue"
					style={{ width: `${value}%` }}
				/>
			</div>
			<span className="w-10 text-right text-xs tabular-nums text-kaefer-dark-grey">
				{present}/{total}
			</span>
		</div>
	)
}
